import { Button } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { UUIDString } from "../../../../../../common/service/server/Types";
import { CollectionGroupDetailsDto } from "../../../../../../common/service/server/dto/CollectionGroupDto";
import { CollectionGroupCollapseCard } from "../../../../../components/CollectionGroupCollapseCard";
import { StandardPageWrapper } from "../../../../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../../../../components/layout/header/Header";
import { usePrimaryOrganizationId, usePrimaryOrganization } from "../../../../../hooks";
import { useCollectionGroups } from "../../../../../hooks/useCollectionGroups";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { useOrganizationVendorDetails } from "../../../../../hooks/useOrganizationVendors";
import { RouteHelpers, Routes } from "../../../../../root/routes";
import { ProgressFooter, ProgressFooterActions } from "../../../../get-compliant/ProgressFooter";
import { DataRecipientCollectionCheckboxes } from "../../../../get-compliant/data-recipients/DataRecipientCollectionCheckboxes";
import { PageHeaderBack } from "../../../../../components/layout/header/PageHeaderBack";
import { DataRecipientLogo } from "../../../../../components/DataRecipientLogo";
import { DeleteForever } from "@mui/icons-material";

type Params = {
  dataRecipientId: UUIDString;
};

const CollectionGroupDisclosureCard = ({
  dataMap,
  patchDataMap,
  collectionGroup,
  dataRecipient,
  collapsed,
  onExpand,
  onNext,
  org,
}) => {
  return (
    <CollectionGroupCollapseCard
      className="mb-md"
      collectionGroup={collectionGroup}
      collapsed={collapsed}
      onExpand={onExpand}
    >
      <DataRecipientCollectionCheckboxes
        recipient={dataRecipient}
        dataMap={dataMap}
        group={collectionGroup}
        patchDataMap={patchDataMap}
        org={org}
      />

      {Boolean(onNext) && (
        <Button variant="contained" onClick={onNext}>
          Next Group
        </Button>
      )}
    </CollectionGroupCollapseCard>
  );
};

export const DisclosureForDataRecipientPage = () => {
  const history = useHistory();
  const org = usePrimaryOrganization();
  const orgId = usePrimaryOrganizationId();
  const { dataRecipientId } = useParams<Params>();

  const {
    data: dataMap,
    request: dataMapRequest,
    mutators: { patch },
  } = useDataMap(orgId);
  const [dataRecipient, dataRecipientRequest] = useOrganizationVendorDetails(
    orgId,
    dataRecipientId,
  );
  const [collectionGroups, collectionGroupsRequest] = useCollectionGroups(orgId);
  const [activeGroup, setActiveGroup] = useState<CollectionGroupDetailsDto>(null);

  const associations = useMemo(
    () => dataMap.recipientAssociations.filter((a) => a.vendorId == dataRecipient?.vendorId),
    [dataMap.recipientAssociations, dataRecipient],
  );

  const groupIds = useMemo(() => associations.map((d) => d.groupId), [associations]);

  const relevantGroups = useMemo(
    () => collectionGroups?.filter((r) => groupIds?.includes(r.id)),
    [collectionGroups, groupIds],
  );

  const handleExpand = (cg: CollectionGroupDetailsDto) => {
    setActiveGroup(cg);
    window.polaris.scrollSelectorIntoView(`#cg-${cg.id}`);
  };

  const allSelected = useMemo(() => {
    return relevantGroups.every((cg) =>
      dataMap.disclosure.some((d) => d.groupId == cg.id && d.vendorId == dataRecipient?.vendorId),
    );
  }, [relevantGroups, dataRecipient, dataMap.disclosure]);

  // default expand the active group
  useEffect(() => {
    if (relevantGroups[0] && !activeGroup) {
      setActiveGroup(relevantGroups[0]);
    }
  }, [activeGroup, relevantGroups]);

  return (
    <StandardPageWrapper
      requests={[collectionGroupsRequest, dataMapRequest, dataRecipientRequest]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              nextUrl={RouteHelpers.dataMap.dataRecipient.workflow.classification(dataRecipientId)}
              nextContent="Next"
              prevContent="Back"
              nextDisabled={!allSelected || patch.isLoading}
              prevUrl={RouteHelpers.dataMap.dataRecipient.workflow.groupAssociation(
                dataRecipientId,
              )}
              prevOnClick={() =>
                history.push(
                  RouteHelpers.dataMap.dataRecipient.workflow.groupAssociation(dataRecipientId),
                )
              }
            />
          }
        />
      }
    >
      <PageHeader
        titleContent={dataRecipient?.name}
        titleHeaderContent={
          <PageHeaderBack to={`${Routes.dataMap.root}?tab=data_recipients`}>
            Back to Data Map
          </PageHeaderBack>
        }
        logo={<DataRecipientLogo dataRecipient={dataRecipient} size={48} />}
        actions={
          <>
            <Button
              onClick={() => history.push(Routes.dataMap.root)}
              color="error"
              startIcon={<DeleteForever />}
              variant="text"
            >
              Remove
            </Button>
          </>
        }
      />

      {relevantGroups.map((cg, idx) => {
        let nextGroup: CollectionGroupDetailsDto | null = null;

        if (idx < relevantGroups.length - 1) {
          nextGroup = relevantGroups[idx + 1];
        }

        return (
          <div key={cg.id} id={`cg-${cg.id}`} className="mb-md">
            <CollectionGroupDisclosureCard
              dataRecipient={dataRecipient}
              dataMap={dataMap}
              patchDataMap={patch}
              collectionGroup={cg}
              collapsed={activeGroup?.id !== cg.id}
              onExpand={() => handleExpand(cg)}
              onNext={nextGroup ? () => handleExpand(nextGroup) : undefined}
              org={org}
            />
          </div>
        );
      })}
    </StandardPageWrapper>
  );
};
